import { Plus, Trash2 } from "lucide-react";
import type { Dispatch, SetStateAction } from "react";
import type { JsonRecord, WorkContext } from "../types";

type BoltItem = {
  especificacion: string;
  diametro: string;
  cantidad: string;
};

function catalogArray(catalog: JsonRecord, candidates: string[]) {
  for (const key of candidates) {
    if (Array.isArray(catalog[key])) return catalog[key] as JsonRecord[];
  }
  return [];
}

function optionValue(item: JsonRecord) {
  return String(item.id || item.value || item.valor || item.codigo || item.nombre || "");
}

function optionLabel(item: JsonRecord) {
  return String(item.nombre || item.label || item.descripcion || item.value || item.valor || item.codigo || "");
}

function boltItems(value: unknown): BoltItem[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    const row = (item || {}) as JsonRecord;
    return {
      especificacion: String(row.especificacion || ""),
      diametro: String(row.diametro || ""),
      cantidad: String(row.cantidad || ""),
    };
  });
}

export function prepareBoltPayload(values: JsonRecord) {
  const rows = boltItems(values.tornillos_por_especificacion);
  const completeRows = rows.filter((row) => row.especificacion.trim() || row.diametro.trim() || row.cantidad.trim());
  if (completeRows.some((row) => !row.especificacion.trim() || !row.diametro.trim() || !/^[1-9]\d*$/.test(row.cantidad.trim()))) {
    throw new Error("Complete especificación, diámetro y una cantidad entera mayor que cero en cada renglón.");
  }

  if (!String(values.frente_id || "").trim()) {
    throw new Error("Seleccione un frente.");
  }

  const totalText = String(values.cantidad_total_tornillos || "").trim();
  const inspectedText = String(values.cantidad_tornillos_inspeccionados || "").trim();
  if (totalText && !/^[1-9]\d*$/.test(totalText)) {
    throw new Error("La cantidad total debe ser un entero mayor que cero.");
  }

  const calibratedWrench = values.metodo_pretension === "llave_calibrada";
  if (calibratedWrench && !/^[1-9]\d*$/.test(inspectedText)) {
    throw new Error("Indique la cantidad de tornillos inspeccionados con llave calibrada.");
  }
  if (calibratedWrench && totalText && Number(inspectedText) > Number(totalText)) {
    throw new Error("Los tornillos inspeccionados no pueden exceder la cantidad total.");
  }

  const adjustedTight = values.tipo_conexion === "apriete_ajustado";
  return {
    frente_id: values.frente_id,
    subfrente_id: values.subfrente_id || null,
    entregable_id: values.entregable_id || null,
    ubicacion_conexion: String(values.ubicacion_conexion || "").trim(),
    tipo_conexion: values.tipo_conexion,
    dictamen: values.dictamen,
    metodo_pretension: adjustedTight ? null : values.metodo_pretension || null,
    tornillos_por_especificacion: completeRows.length
      ? completeRows.map((row) => ({
        especificacion: row.especificacion.trim(),
        diametro: row.diametro.trim(),
        cantidad: Number(row.cantidad),
      }))
      : null,
    cantidad_total_tornillos: totalText ? Number(totalText) : null,
    cantidad_tornillos_inspeccionados: calibratedWrench ? Number(inspectedText) : null,
    comentarios: String(values.comentarios || "").trim() || null,
  };
}

export function BoltInspectionFields({
  values,
  setValues,
  catalog,
}: {
  values: JsonRecord;
  setValues: Dispatch<SetStateAction<JsonRecord>>;
  catalog: JsonRecord;
  context: WorkContext;
}) {
  const scopedFronts = catalogArray(catalog, ["frentes_creacion", "frentes_asignados", "frentes_alcance"]);
  const hasScopedFronts = ["frentes_creacion", "frentes_asignados", "frentes_alcance"].some((key) => Array.isArray(catalog[key]));
  const frontCatalog = hasScopedFronts ? scopedFronts : catalogArray(catalog, ["frentes", "fronts"]);
  const fronts = values.frente_id && !frontCatalog.some((item) => optionValue(item) === String(values.frente_id))
    ? [{ id: values.frente_id, nombre: values.frente || "Frente actual" }, ...frontCatalog]
    : frontCatalog;
  const scopedSubfronts = catalogArray(catalog, ["subfrentes_creacion", "subfrentes_asignados", "subfrentes_alcance"]);
  const hasScopedSubfronts = ["subfrentes_creacion", "subfrentes_asignados", "subfrentes_alcance"].some((key) => Array.isArray(catalog[key]));
  const subfrontCatalog = hasScopedSubfronts ? scopedSubfronts : catalogArray(catalog, ["subfrentes", "subfronts"]);
  const availableSubfronts = values.subfrente_id && !subfrontCatalog.some((item) => optionValue(item) === String(values.subfrente_id))
    ? [{ id: values.subfrente_id, frente_id: values.frente_id, nombre: values.subfrente || "Subfrente actual" }, ...subfrontCatalog]
    : subfrontCatalog;
  const subfronts = availableSubfronts
    .filter((item) => !values.frente_id || String(item.frente_id || item.frenteId || "") === String(values.frente_id));
  const types = catalogArray(catalog, ["tipos_conexion", "tiposConexion"]);
  const methods = catalogArray(catalog, ["metodos_pretension", "metodosPretension"]);
  const verdicts = catalogArray(catalog, ["dictamenes", "dictamen"]);
  const items = boltItems(values.tornillos_por_especificacion);
  const needsPretension = ["pretensada", "deslizamiento_critico"].includes(String(values.tipo_conexion || ""));
  const calibratedWrench = needsPretension && values.metodo_pretension === "llave_calibrada";

  function set(key: string, value: unknown) {
    setValues((current) => ({ ...current, [key]: value }));
  }

  function setType(value: string) {
    setValues((current) => ({
      ...current,
      tipo_conexion: value,
      metodo_pretension: value === "apriete_ajustado" ? "" : current.metodo_pretension,
      cantidad_tornillos_inspeccionados: value === "apriete_ajustado" ? "" : current.cantidad_tornillos_inspeccionados,
    }));
  }

  function setMethod(value: string) {
    setValues((current) => ({
      ...current,
      metodo_pretension: value,
      cantidad_tornillos_inspeccionados: value === "llave_calibrada" ? current.cantidad_tornillos_inspeccionados : "",
    }));
  }

  function updateItem(index: number, key: keyof BoltItem, value: string) {
    const next = items.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item);
    set("tornillos_por_especificacion", next);
  }

  function addItem() {
    set("tornillos_por_especificacion", [...items, { especificacion: "", diametro: "", cantidad: "" }]);
  }

  function removeItem(index: number) {
    set("tornillos_por_especificacion", items.filter((_, itemIndex) => itemIndex !== index));
  }

  const typeFallback = [
    { valor: "apriete_ajustado", nombre: "De apriete ajustado" },
    { valor: "pretensada", nombre: "Pretensada" },
    { valor: "deslizamiento_critico", nombre: "De deslizamiento crítico" },
  ];
  const methodFallback = [
    { valor: "giro_tuerca", nombre: "Giro de la tuerca" },
    { valor: "llave_calibrada", nombre: "Llave calibrada" },
    { valor: "indicador_tension_directa", nombre: "Indicador de tensión directa" },
    { valor: "tornillo_diseno_especial", nombre: "Tornillo de diseño especial" },
    { valor: "metodo_combinado", nombre: "Método combinado" },
  ];

  return <>
    <section className={`bolt-main-fields full${calibratedWrench ? " calibrated" : ""}`}>
      <label className="bolt-front">Frente<select value={String(values.frente_id || "")} onChange={(event) => setValues((current) => ({ ...current, frente_id: event.target.value, subfrente_id: "" }))} required><option value="">Selecciona…</option>{fronts.map((item) => <option key={optionValue(item)} value={optionValue(item)}>{optionLabel(item)}</option>)}</select></label>
      <label className="bolt-subfront">Subfrente<select value={String(values.subfrente_id || "")} onChange={(event) => set("subfrente_id", event.target.value)} disabled={!values.frente_id}><option value="">Sin subfrente</option>{subfronts.map((item) => <option key={optionValue(item)} value={optionValue(item)}>{optionLabel(item)}</option>)}</select></label>
      <label className="bolt-location">Ubicación de la conexión<input value={String(values.ubicacion_conexion || "")} onChange={(event) => set("ubicacion_conexion", event.target.value)} placeholder="Eje, nivel, tramo o elemento" required /></label>
      <label className="bolt-type">Tipo de conexión<select value={String(values.tipo_conexion || "")} onChange={(event) => setType(event.target.value)} required><option value="">Selecciona…</option>{(types.length ? types : typeFallback).map((item) => <option key={optionValue(item)} value={optionValue(item)}>{optionLabel(item)}</option>)}</select></label>
      <label className="bolt-method">Método de pretensión<select value={String(values.metodo_pretension || "")} onChange={(event) => setMethod(event.target.value)} required={needsPretension} disabled={!needsPretension}><option value="">{needsPretension ? "Selecciona…" : "No aplica"}</option>{(methods.length ? methods : methodFallback).map((item) => <option key={optionValue(item)} value={optionValue(item)}>{optionLabel(item)}</option>)}</select></label>
      <label className="bolt-total">Total de tornillos<input type="number" min="1" step="1" value={String(values.cantidad_total_tornillos || "")} onChange={(event) => set("cantidad_total_tornillos", event.target.value)} placeholder="Ej. 24" /></label>
      {calibratedWrench && <label className="bolt-inspected">Tornillos inspeccionados<input type="number" min="1" step="1" max={String(values.cantidad_total_tornillos || "") || undefined} value={String(values.cantidad_tornillos_inspeccionados || "")} onChange={(event) => set("cantidad_tornillos_inspeccionados", event.target.value)} required /></label>}
      <label className="bolt-verdict">Dictamen de la junta<select value={String(values.dictamen || "")} onChange={(event) => set("dictamen", event.target.value)} required><option value="">Selecciona…</option>{(verdicts.length ? verdicts : [{ valor: "aceptada", nombre: "Aceptada" }, { valor: "rechazada", nombre: "Rechazada" }]).map((item) => <option key={optionValue(item)} value={optionValue(item)}>{optionLabel(item)}</option>)}</select></label>
    </section>

    <section className="bolt-breakdown full">
      <header>
        <div><strong>Tornillos por especificación y diámetro</strong><small>Agrega únicamente los grupos que necesites.</small></div>
        <button type="button" className="button secondary compact" onClick={addItem}><Plus size={15} /> Agregar renglón</button>
      </header>
      {items.length ? <div className="bolt-breakdown-list">
        <div className="bolt-breakdown-head"><span>Especificación</span><span>Diámetro</span><span>Cantidad</span><span aria-hidden="true"></span></div>
        {items.map((item, index) => <div className="bolt-breakdown-row" key={index}>
          <input aria-label={`Especificación ${index + 1}`} value={item.especificacion} onChange={(event) => updateItem(index, "especificacion", event.target.value)} placeholder="Ej. ASTM F3125 Gr. A325" required />
          <input aria-label={`Diámetro ${index + 1}`} value={item.diametro} onChange={(event) => updateItem(index, "diametro", event.target.value)} placeholder="Ej. 3/4 in" required />
          <input aria-label={`Cantidad ${index + 1}`} type="number" min="1" step="1" value={item.cantidad} onChange={(event) => updateItem(index, "cantidad", event.target.value)} placeholder="0" required />
          <button type="button" className="icon-button danger" onClick={() => removeItem(index)} aria-label={`Eliminar renglón ${index + 1}`}><Trash2 size={16} /></button>
        </div>)}
      </div> : <button type="button" className="bolt-breakdown-empty" onClick={addItem}>No hay desglose capturado. <strong>Agregar el primero</strong></button>}
    </section>

    <label className="bolt-comments full">Comentarios<textarea rows={3} value={String(values.comentarios || "")} onChange={(event) => set("comentarios", event.target.value)} placeholder="Observaciones, hallazgos o acciones de seguimiento" /></label>
  </>;
}
